import React, { useCallback, useMemo, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import { Code as CodeIcon } from '@mui/icons-material';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, false] }],
  [{ font: [] }],
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['link', 'image'],
  ['clean'],
];

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter content…',
  minHeight = 200,
}) => {
  const [sourceMode, setSourceMode] = useState(false);

  const modules = useMemo(
    () => ({
      toolbar: TOOLBAR_OPTIONS,
    }),
    [],
  );

  const handleQuillChange = useCallback(
    (content: string) => {
      onChange(content);
    },
    [onChange],
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title={sourceMode ? 'Visual editor' : 'HTML source'}>
        <IconButton
          size="small"
          onClick={() => setSourceMode((s) => !s)}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 10,
            bgcolor: sourceMode ? 'primary.main' : 'action.hover',
            color: sourceMode ? 'primary.contrastText' : 'text.secondary',
            '&:hover': { bgcolor: sourceMode ? 'primary.dark' : 'action.selected' },
          }}
        >
          <CodeIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {sourceMode ? (
        <TextField
          multiline
          fullWidth
          minRows={8}
          maxRows={20}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="<p>Enter HTML…</p>"
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
        />
      ) : (
        <Box
          sx={{
            '& .ql-container': { minHeight, fontSize: 14 },
            '& .ql-editor': { minHeight },
            '& .ql-toolbar': { borderRadius: '4px 4px 0 0' },
            '& .ql-container.ql-snow': { borderRadius: '0 0 4px 4px' },
          }}
        >
          <ReactQuill
            theme="snow"
            value={value}
            onChange={handleQuillChange}
            modules={modules}
            placeholder={placeholder}
          />
        </Box>
      )}
    </Box>
  );
};

export default RichTextEditor;
